/**
 * Disclosure Pre-Validation Service
 *
 * Validates required fields locally before sending disclosures.
 * Shows warnings for missing fields but allows user to proceed.
 * Full validation happens via Encompass API audit when sending.
 */

import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('DisclosurePreValidationService', 'disclosure-dashboard');

/**
 * Required field definition for disclosure audit
 */
export interface RequiredField {
  /** Encompass field ID */
  fieldId: string;
  /** Human-readable field name */
  fieldName: string;
  /** Column name in encompass_loans table */
  localColumn: string;
  /** Description of what this field is used for */
  description?: string;
}

/**
 * Result of pre-validation check
 */
export interface PreValidationResult {
  /** True if all required fields are present */
  isComplete: boolean;
  /** Missing fields that should be filled in Encompass */
  missingFields: RequiredField[];
  /** True if validation was done locally (vs API) */
  checkedLocally: boolean;
  /** Total number of required fields */
  totalRequired: number;
}

/**
 * Required fields for disclosure audit
 * Based on Encompass disclosure audit requirements
 */
export const DISCLOSURE_REQUIRED_FIELDS: RequiredField[] = [
  {
    fieldId: '3',
    fieldName: 'Interest Rate',
    localColumn: 'trans_details_interest_rate',
    description: 'The interest rate for the loan'
  },
  {
    fieldId: '4',
    fieldName: 'Loan Term',
    localColumn: 'loan_term_months',
    description: 'The term of the loan in months'
  },
  {
    fieldId: '325',
    fieldName: 'Due In Months',
    localColumn: 'trans_details_term_due_in',
    description: 'Number of months until loan is due'
  },
  {
    fieldId: '682',
    fieldName: 'First Payment Date',
    localColumn: 'first_payment_date',
    description: 'Date of first payment'
  },
  {
    fieldId: '1612',
    fieldName: 'Loan Originator Name',
    localColumn: 'loan_originator_name',
    description: 'Name of the loan originator for disclosures'
  },
  {
    fieldId: '3238',
    fieldName: 'NMLS LO ID',
    localColumn: 'nmls_loan_originator_id',
    description: 'NMLS ID of the loan originator'
  },
  {
    fieldId: 'L244',
    fieldName: 'Interest From Date',
    localColumn: 'fees_interest_from_date',
    description: 'Date from which interest is calculated'
  }
];

/**
 * Check if a value is considered "present" (not null, undefined, empty string, or 0 for numeric fields)
 */
function isFieldPresent(value: unknown, fieldId: string): boolean {
  if (value === null || value === undefined) {
return false;
}
  if (typeof value === 'string' && value.trim() === '') {
return false;
}
  // For numeric fields, 0 could be valid (e.g., 0% interest) so we allow it
  // The exception would be loan term which should never be 0
  if (fieldId === '4' && value === 0) {
return false;
}
  return true;
}

/**
 * Pre-validate disclosure fields from local loan data
 *
 * @param loanData - Loan data object with column names as keys
 * @returns PreValidationResult with missing fields
 */
export function preValidateDisclosure(
  loanData: Record<string, unknown>
): PreValidationResult {
  const missingFields: RequiredField[] = [];

  for (const field of DISCLOSURE_REQUIRED_FIELDS) {
    const value = loanData[field.localColumn];
    if (!isFieldPresent(value, field.fieldId)) {
      missingFields.push(field);
    }
  }

  const result: PreValidationResult = {
    isComplete: missingFields.length === 0,
    missingFields,
    checkedLocally: true,
    totalRequired: DISCLOSURE_REQUIRED_FIELDS.length
  };

  if (missingFields.length > 0) {
    log.info('Pre-validation found missing fields', {
      missingCount: missingFields.length,
      missingFields: missingFields.map(f => f.fieldId)
    });
  }

  return result;
}

/**
 * Get a summary string of missing fields
 */
export function getMissingFieldsSummary(result: PreValidationResult): string {
  if (result.isComplete) {
    return 'All required fields are present';
  }

  const count = result.missingFields.length;
  const total = result.totalRequired;
  const fieldList = result.missingFields.map(f => f.fieldName).join(', ');

  return `${count} of ${total} required fields missing: ${fieldList}`;
}
